import { Controller } from "@hotwired/stimulus"
import MediumEditor from 'medium-editor'
import 'medium-editor/dist/css/medium-editor.css'
import 'medium-editor/dist/css/themes/default.css'
import fountain from "../vendor/fountain"

export default class extends Controller {
  static targets = [
    "writingArea",
    "chapterContainer",
    "editor",
    "wordCount",
    "totalWordCount",
    "saveStatus",
    "progressBar",
    "chapterNavItem",
    "expandButton",
    "expandIcon",
    "expandText",
    "sidebar",
    "sidebarHeader",
    "sidebarTitle",
    "sidebarCount",
    "sidebarToggleIcon",
    "sidebarList",
    "sidebarFooter",
    "sidebarFooterMinimal",
    "chapterNavFull",
    "chapterNavMinimal",
    "coachPanel",
    "showCoachButton",
    "feedbackContainer",
    "titlePage",
    "previewButton",
    "previewIcon",
    "previewText",
    "previewContainer",
    "titleField",
    "authorField",
    "contactField",
    "titlePageSaveStatus"
  ]

  static values = {
    projectId: String,
    updateUrl: String,
    unitName: { type: String, default: "Chapter" }
  }

  // Fountain autocomplete suggestions
  static SCENE_HEADING_SUGGESTIONS = [
    { trigger: "I", completion: "INT. ", label: "INT." },
    { trigger: "E", completion: "EXT. ", label: "EXT." },
    { trigger: "IN", completion: "INT. ", label: "INT." },
    { trigger: "INT", completion: "INT. ", label: "INT." },
    { trigger: "EX", completion: "EXT. ", label: "EXT." },
    { trigger: "EXT", completion: "EXT. ", label: "EXT." },
    { trigger: "INT./EXT", completion: "INT./EXT. ", label: "INT./EXT." },
    { trigger: "INT/EXT", completion: "INT./EXT. ", label: "INT./EXT." },
    { trigger: "I/E", completion: "I/E. ", label: "I/E." }
  ]

  static TIME_OF_DAY_SUGGESTIONS = [
    { label: "DAY", completion: "DAY" },
    { label: "NIGHT", completion: "NIGHT" },
    { label: "MORNING", completion: "MORNING" },
    { label: "EVENING", completion: "EVENING" },
    { label: "CONTINUOUS", completion: "CONTINUOUS" },
    { label: "LATER", completion: "LATER" },
    { label: "MOMENTS LATER", completion: "MOMENTS LATER" }
  ]

  connect() {
    this.saveTimers = {} // Per-chapter save debounce timers
    this.pendingSaves = new Set() // Track chapters with pending saves
    this.initializedEditors = new Set() // Track which editors have been initialized
    this.autocompleteDropdown = null
    this.selectedAutocompleteIndex = 0
    this.currentAutocompleteEditor = null
    this.locationJustCompleted = false // Track if we just completed a location

    // UI state
    this.expandedChapterPosition = null // Track which chapter is expanded (null = none)
    this.sidebarMinimized = false
    this.sidebarMinimizedByExpand = false // Track if sidebar was minimized by expand action
    this.coachPanelHidden = false

    // Initialize visible editors using IntersectionObserver
    this.setupLazyLoading()

    // Initialize the first 3 editors immediately
    this.initializeFirstEditors(3)

    // Setup autocomplete dropdown
    this.createAutocompleteDropdown()

    // Close dropdown when clicking outside
    this.handleClickOutside = this.handleClickOutside.bind(this)
    document.addEventListener("click", this.handleClickOutside)
  }

  disconnect() {
    // Clear all save timers
    Object.values(this.saveTimers).forEach(timer => clearTimeout(timer))

    // Disconnect observer
    if (this.observer) {
      this.observer.disconnect()
    }

    // Destroy MediumEditor instances
    if (this.mediumEditors) {
      this.mediumEditors.forEach(editor => editor.destroy())
      this.mediumEditors.clear()
    }

    // Remove autocomplete dropdown
    if (this.autocompleteDropdown) {
      this.autocompleteDropdown.remove()
    }

    // Remove click outside listener
    document.removeEventListener("click", this.handleClickOutside)
  }

  handleClickOutside(event) {
    if (this.autocompleteDropdown &&
        !this.autocompleteDropdown.contains(event.target) &&
        !event.target.matches("textarea[data-editor-type='fountain']")) {
      this.hideAutocomplete()
    }
  }

  createAutocompleteDropdown() {
    this.autocompleteDropdown = document.createElement("div")
    this.autocompleteDropdown.className = "fixed z-50 bg-white border border-gray-300 rounded-lg shadow-lg py-1 hidden min-w-32"
    document.body.appendChild(this.autocompleteDropdown)
  }

  handleKeydown(event) {
    const editor = event.target
    if (editor.dataset.editorType !== "fountain") return

    // Handle autocomplete navigation
    if (!this.autocompleteDropdown.classList.contains("hidden")) {
      const items = this.autocompleteDropdown.querySelectorAll("[data-autocomplete-item]")

      if (event.key === "ArrowDown") {
        event.preventDefault()
        this.selectedAutocompleteIndex = Math.min(this.selectedAutocompleteIndex + 1, items.length - 1)
        this.updateAutocompleteSelection(items)
      } else if (event.key === "ArrowUp") {
        event.preventDefault()
        this.selectedAutocompleteIndex = Math.max(this.selectedAutocompleteIndex - 1, 0)
        this.updateAutocompleteSelection(items)
      } else if (event.key === "Enter" || event.key === "Tab") {
        if (items.length > 0) {
          event.preventDefault()
          this.selectAutocomplete(items[this.selectedAutocompleteIndex])
        }
      } else if (event.key === "Escape") {
        this.hideAutocomplete()
      }
    }
  }

  updateAutocompleteSelection(items) {
    items.forEach((item, index) => {
      if (index === this.selectedAutocompleteIndex) {
        item.classList.add("bg-primary-100")
      } else {
        item.classList.remove("bg-primary-100")
      }
    })
  }

  checkForAutocomplete(editor) {
    if (editor.dataset.editorType !== "fountain") return

    const cursorPos = editor.selectionStart
    const text = editor.value
    const lineStart = text.lastIndexOf("\n", cursorPos - 1) + 1
    const currentLine = text.substring(lineStart, cursorPos)
    const currentLineUpper = currentLine.toUpperCase()

    // Stage 1: Check for scene heading triggers (I, E, INT, EXT at start of line)
    const sceneMatch = currentLineUpper.match(/^(I|E|IN|INT\.?|EX|EXT\.?|INT\.?\/?\s*EXT\.?|I\/E\.?)$/i)
    if (sceneMatch) {
      this.locationJustCompleted = false // Reset flag
      const trigger = sceneMatch[1].toUpperCase().replace(/\.$/, "").replace(/\s+/g, "")

      // Get unique suggestions that match the trigger
      const seen = new Set()
      const suggestions = this.constructor.SCENE_HEADING_SUGGESTIONS.filter(s => {
        if (seen.has(s.label)) return false
        const matches = s.trigger.startsWith(trigger) || trigger.startsWith(s.trigger)
        if (matches) seen.add(s.label)
        return matches
      })

      if (suggestions.length > 0) {
        this.showAutocomplete(editor, suggestions, lineStart, cursorPos)
        return
      }
    }

    // Stage 2: Check for location (after "INT. " or "EXT. " etc, before " - ")
    // Only trigger if at least one character typed after space, and location wasn't just completed
    const locationMatch = currentLineUpper.match(/^(INT\.|EXT\.|INT\.\/EXT\.|I\/E\.)\s+([A-Z][A-Z\s]*)$/i)
    if (locationMatch && !this.locationJustCompleted) {
      const partial = locationMatch[2].trim()
      const locations = this.getLocationsForEditor(editor)
      // Full-text search: match if partial appears anywhere in the location name
      const suggestions = locations
        .filter(loc => loc.includes(partial) && loc !== partial)
        .slice(0, 6)
        .map(loc => ({ label: loc, completion: loc + " - ", isLocation: true }))

      if (suggestions.length > 0) {
        const locationStartPos = lineStart + locationMatch[1].length + 1
        this.showAutocomplete(editor, suggestions, locationStartPos, cursorPos)
        return
      }
    }

    // Stage 3: Check for time of day trigger (after " - " at end of scene heading)
    const timeMatch = currentLineUpper.match(/^(INT\.|EXT\.|INT\.\/EXT\.|I\/E\.)\s+.+\s+-\s*([A-Z]*)$/i)
    if (timeMatch) {
      this.locationJustCompleted = false // Reset flag when hyphen is typed
      const partial = timeMatch[2]
      const suggestions = this.constructor.TIME_OF_DAY_SUGGESTIONS.filter(s =>
        s.label.startsWith(partial)
      )
      if (suggestions.length > 0) {
        const dashPos = currentLine.lastIndexOf(" - ") + 3
        this.showAutocomplete(editor, suggestions, lineStart + dashPos, cursorPos)
        return
      }
    }

    // Check for character name (all caps, 2+ chars, not a scene heading)
    const charMatch = currentLine.match(/^([A-Z]{2,})$/)
    if (charMatch && !currentLineUpper.match(/^(INT|EXT|I\/E)/)) {
      const partial = charMatch[1]
      const characters = this.getCharactersForEditor(editor)
      const suggestions = characters
        .filter(name => name.startsWith(partial) && name !== partial)
        .map(name => ({ label: name, completion: name }))

      if (suggestions.length > 0) {
        this.showAutocomplete(editor, suggestions, lineStart, cursorPos)
        return
      }
    }

    this.hideAutocomplete()
  }

  getCharactersForEditor(editor) {
    try {
      const data = editor.dataset.characters
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  }

  getLocationsForEditor(editor) {
    try {
      const data = editor.dataset.locations
      return data ? JSON.parse(data) : []
    } catch {
      return []
    }
  }

  showAutocomplete(editor, suggestions, replaceStart, replaceEnd) {
    this.currentAutocompleteEditor = editor
    this.autocompleteReplaceStart = replaceStart
    this.autocompleteReplaceEnd = replaceEnd
    this.selectedAutocompleteIndex = 0

    // Get cursor position for dropdown placement
    const rect = editor.getBoundingClientRect()
    const lineHeight = parseInt(getComputedStyle(editor).lineHeight) || 24
    const text = editor.value.substring(0, editor.selectionStart)
    const lines = text.split("\n")
    const currentLineNum = lines.length - 1

    // Calculate position - place dropdown to the right of where user is typing
    // Account for scroll position within the textarea
    const scrollTop = editor.scrollTop
    const lineTop = (currentLineNum * lineHeight) - scrollTop

    // Position below the current line, offset to the right
    const top = rect.top + lineTop + lineHeight + 4 + window.scrollY
    const left = rect.left + 200 // Offset to the right so it doesn't cover typing

    // Build dropdown content
    this.autocompleteDropdown.innerHTML = suggestions.map((s, index) => `
      <div
        data-autocomplete-item
        data-completion="${s.completion}"
        data-is-location="${s.isLocation || false}"
        class="px-3 py-1.5 cursor-pointer text-sm font-mono hover:bg-primary-100 ${index === 0 ? 'bg-primary-100' : ''}"
      >
        ${s.label}
      </div>
    `).join("")

    // Add click handlers
    this.autocompleteDropdown.querySelectorAll("[data-autocomplete-item]").forEach(item => {
      item.addEventListener("click", (e) => {
        e.stopPropagation()
        this.selectAutocomplete(item)
      })
    })

    // Position and show
    this.autocompleteDropdown.style.top = `${top}px`
    this.autocompleteDropdown.style.left = `${left}px`
    this.autocompleteDropdown.classList.remove("hidden")
  }

  hideAutocomplete() {
    if (this.autocompleteDropdown) {
      this.autocompleteDropdown.classList.add("hidden")
    }
    this.currentAutocompleteEditor = null
  }

  selectAutocomplete(item) {
    if (!this.currentAutocompleteEditor) return

    const editor = this.currentAutocompleteEditor
    const completion = item.dataset.completion
    const isLocation = item.dataset.isLocation === "true"
    const text = editor.value

    // Replace the partial text with the completion
    const before = text.substring(0, this.autocompleteReplaceStart)
    const after = text.substring(this.autocompleteReplaceEnd)
    editor.value = before + completion + after

    // Move cursor to end of completion
    const newPos = this.autocompleteReplaceStart + completion.length
    editor.selectionStart = newPos
    editor.selectionEnd = newPos
    editor.focus()

    // If we just completed a location, set flag to prevent reopening until hyphen
    if (isLocation) {
      this.locationJustCompleted = true
    }

    this.hideAutocomplete()

    // Trigger input event to save (but skip autocomplete check)
    this.skipNextAutocomplete = true
    editor.dispatchEvent(new Event("input", { bubbles: true }))
    this.skipNextAutocomplete = false
  }

  setupLazyLoading() {
    // Intersection observer for lazy loading editors
    this.observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const editor = entry.target
            const cardId = editor.dataset.cardId
            if (!this.initializedEditors.has(cardId)) {
              this.initializeEditor(editor)
              this.initializedEditors.add(cardId)
            }
          }
        })
      },
      {
        rootMargin: "200px", // Start loading when within 200px of viewport
        threshold: 0
      }
    )

    // Observe all editors
    this.editorTargets.forEach(editor => {
      this.observer.observe(editor)
    })
  }

  initializeFirstEditors(count) {
    const editors = this.editorTargets.slice(0, count)
    editors.forEach(editor => {
      const cardId = editor.dataset.cardId
      if (!this.initializedEditors.has(cardId)) {
        this.initializeEditor(editor)
        this.initializedEditors.add(cardId)
      }
    })
  }

  initializeEditor(editor) {
    const editorType = editor.dataset.editorType

    if (editorType === "novel") {
      // Initialize MediumEditor for rich text editing
      this.initializeMediumEditor(editor)
    }
  }

  initializeMediumEditor(editor) {
    // Store MediumEditor instances
    if (!this.mediumEditors) {
      this.mediumEditors = new Map()
    }

    const cardId = editor.dataset.cardId

    // Don't re-initialize if already done
    if (this.mediumEditors.has(cardId)) return

    const mediumEditor = new MediumEditor(editor, {
      placeholder: {
        text: 'Start writing your chapter here...',
        hideOnClick: true
      },
      toolbar: {
        buttons: [
          {
            name: 'justifyLeft',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="15" x2="3" y1="12" y2="12"/><line x1="17" x2="3" y1="18" y2="18"/></svg>',
          },
          {
            name: 'justifyCenter',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="17" x2="7" y1="12" y2="12"/><line x1="19" x2="5" y1="18" y2="18"/></svg>',
          },
          {
            name: 'justifyRight',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="21" x2="3" y1="6" y2="6"/><line x1="21" x2="9" y1="12" y2="12"/><line x1="21" x2="7" y1="18" y2="18"/></svg>',
          },
          {
            name: 'h1',
            contentDefault: '<span style="font-size: 18px; font-weight: bold;">H1</span>',
          },
          {
            name: 'h2',
            contentDefault: '<span style="font-size: 14px; font-weight: bold;">H2</span>',
          },
          {
            name: 'h6',
            action: 'append-p',
            aria: 'paragraph',
            tagNames: ['p'],
            contentDefault: '<b>P</b>',
          },
          {
            name: 'bold',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 12a4 4 0 0 0 0-8H6v8"/><path d="M15 20a4 4 0 0 0 0-8H6v8Z"/></svg>',
          },
          {
            name: 'italic',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="19" x2="10" y1="4" y2="4"/><line x1="14" x2="5" y1="20" y2="20"/><line x1="15" x2="9" y1="4" y2="20"/></svg>',
          },
          {
            name: 'underline',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M6 4v6a6 6 0 0 0 12 0V4"/><line x1="4" x2="20" y1="20" y2="20"/></svg>',
          },
          {
            name: 'quote',
            contentDefault: '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 21c3 0 7-1 7-8V5c0-1.25-.756-2.017-2-2H4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2 1 0 1 0 1 1v1c0 1-1 2-2 2s-1 .008-1 1.031V21z"/><path d="M15 21c3 0 7-1 7-8V5c0-1.25-.757-2.017-2-2h-4c-1.25 0-2 .75-2 1.972V11c0 1.25.75 2 2 2h.75c0 2.25.25 4-2.75 4v3z"/></svg>',
          },
        ]
      },
      keyboardCommands: {
        commands: [
          { command: 'bold', key: 'B', meta: true, shift: false, alt: false },
          { command: 'italic', key: 'I', meta: true, shift: false, alt: false },
          { command: 'underline', key: 'U', meta: true, shift: false, alt: false },
          { command: 'append-blockquote', key: 'B', meta: true, shift: true, alt: false },
          { command: 'append-h2', key: 'H', meta: true, shift: true, alt: false },
        ],
      }
    })

    // Subscribe to content changes
    mediumEditor.subscribe('editableInput', () => {
      const chapterId = editor.dataset.chapterId
      this.updateWordCount(editor)
      this.debounceSave(cardId, chapterId, editor)
      this.showSaveStatus(cardId, "Saving...")
    })

    this.mediumEditors.set(cardId, mediumEditor)
  }

  handleInput(event) {
    const editor = event.target
    const cardId = editor.dataset.cardId
    const chapterId = editor.dataset.chapterId
    const editorType = editor.dataset.editorType

    // Novel editors are handled by MediumEditor subscription
    if (editorType === "novel") return

    // Update word count immediately
    this.updateWordCount(editor)

    // Debounce save
    this.debounceSave(cardId, chapterId, editor)

    // Show "saving" status
    this.showSaveStatus(cardId, "Saving...")

    // Check for autocomplete (Fountain editors only), unless we should skip
    if (!this.skipNextAutocomplete) {
      this.checkForAutocomplete(editor)
    }
  }

  // Check if novel editor is empty and toggle placeholder class
  updatePlaceholderState(editor) {
    const text = editor.innerText.trim()
    const isEmpty = text === "" || text === "\n"
    editor.classList.toggle("is-empty", isEmpty)
  }

  handleBlur(event) {
    const editor = event.target
    const cardId = editor.dataset.cardId
    const chapterId = editor.dataset.chapterId

    // Hide autocomplete (with slight delay to allow click selection)
    setTimeout(() => this.hideAutocomplete(), 150)

    // Immediate save on blur
    if (this.pendingSaves.has(cardId)) {
      clearTimeout(this.saveTimers[cardId])
      this.saveChapter(cardId, chapterId, editor)
    }
  }

  debounceSave(cardId, chapterId, editor) {
    this.pendingSaves.add(cardId)

    // Clear existing timer
    if (this.saveTimers[cardId]) {
      clearTimeout(this.saveTimers[cardId])
    }

    // Set new timer (1.5s debounce)
    this.saveTimers[cardId] = setTimeout(() => {
      this.saveChapter(cardId, chapterId, editor)
    }, 1500)
  }

  async saveChapter(cardId, chapterId, editor) {
    this.pendingSaves.delete(cardId)

    const body = this.getEditorContent(editor)
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    try {
      const response = await fetch(`${this.updateUrlValue}/${cardId}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          chapter: { body: body }
        })
      })

      const data = await response.json()

      if (data.success) {
        this.showSaveStatus(cardId, "Saved")
        if (data.word_count !== undefined) {
          this.updateWordCountDisplay(cardId, data.word_count)
        }
        // Update totals
        this.updateTotalWordCount()
      } else {
        this.showSaveStatus(cardId, "Error saving")
        console.error("Save failed:", data.errors)
      }
    } catch (error) {
      this.showSaveStatus(cardId, "Error saving")
      console.error("Save error:", error)
    }
  }

  getEditorContent(editor) {
    const editorType = editor.dataset.editorType
    if (editorType === "novel") {
      // For contenteditable, get innerHTML
      return editor.innerHTML
    } else {
      // For textarea (fountain), get value
      return editor.value
    }
  }

  updateWordCount(editor) {
    const content = this.getEditorContent(editor)
    const editorType = editor.dataset.editorType
    const cardId = editor.dataset.cardId

    let wordCount = 0
    if (editorType === "novel") {
      // Strip HTML tags for word count
      const text = content.replace(/<[^>]*>/g, " ")
      wordCount = text.split(/\s+/).filter(word => word.length > 0).length
    } else {
      // Plain text
      wordCount = content.split(/\s+/).filter(word => word.length > 0).length
    }

    this.updateWordCountDisplay(cardId, wordCount)
  }

  updateWordCountDisplay(cardId, wordCount) {
    const countEl = this.wordCountTargets.find(el => el.dataset.cardId === cardId)
    if (countEl) {
      countEl.textContent = wordCount
    }
  }

  updateTotalWordCount() {
    let total = 0
    this.wordCountTargets.forEach(el => {
      const count = parseInt(el.textContent, 10) || 0
      total += count
    })

    if (this.hasTotalWordCountTarget) {
      this.totalWordCountTarget.textContent = total.toLocaleString()
    }
  }

  showSaveStatus(cardId, message) {
    const statusEl = this.saveStatusTargets.find(el => el.dataset.cardId === cardId)
    if (statusEl) {
      statusEl.textContent = message
      statusEl.classList.remove("hidden")

      // Hide after 2 seconds if saved
      if (message === "Saved") {
        setTimeout(() => {
          statusEl.classList.add("hidden")
        }, 2000)
      }
    }
  }

  // Save title page field (title, author_name, contact_info)
  async saveTitlePageField(event) {
    const field = event.target
    const fieldName = field.dataset.field
    const value = field.innerText.trim()
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content

    // Show saving status
    this.showTitlePageSaveStatus("Saving...")

    try {
      const response = await fetch(`/projects/${this.projectIdValue}`, {
        method: "PATCH",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken,
          "X-Requested-With": "XMLHttpRequest",
          "Accept": "application/json"
        },
        credentials: "same-origin",
        body: JSON.stringify({
          project: { [fieldName]: value }
        })
      })

      if (response.ok) {
        this.showTitlePageSaveStatus("Saved")
      } else {
        const data = await response.json()
        this.showTitlePageSaveStatus("Error saving")
        console.error("Title page save failed:", data.errors)
      }
    } catch (error) {
      this.showTitlePageSaveStatus("Error saving")
      console.error("Title page save error:", error)
    }
  }

  showTitlePageSaveStatus(message) {
    if (this.hasTitlePageSaveStatusTarget) {
      this.titlePageSaveStatusTarget.textContent = message

      // Hide after 2 seconds if saved
      if (message === "Saved") {
        setTimeout(() => {
          this.titlePageSaveStatusTarget.textContent = ""
        }, 2000)
      }
    }
  }

  scrollToChapter(event) {
    const position = event.currentTarget.dataset.chapterPosition

    // If in expanded mode, switch to the clicked chapter (slideshow behavior)
    if (this.expandedChapterPosition !== null) {
      this.switchToChapter(position)
      return
    }

    // Normal scroll behavior when not expanded
    const container = document.getElementById(`chapter-${position}`)

    if (container) {
      container.scrollIntoView({ behavior: "smooth", block: "start" })

      // Highlight active nav item
      this.chapterNavItemTargets.forEach(item => {
        item.classList.remove("bg-primary-100")
      })
      event.currentTarget.classList.add("bg-primary-100")
    }
  }

  // Switch to a specific chapter in expanded/slideshow mode
  switchToChapter(position) {
    // Update the expanded chapter position
    this.expandedChapterPosition = position

    // Hide all chapters except the target
    this.chapterContainerTargets.forEach(container => {
      if (container.dataset.cardPosition === position) {
        container.classList.remove("hidden")
        container.classList.add("expanded-chapter")
      } else {
        container.classList.add("hidden")
        container.classList.remove("expanded-chapter")
      }
    })

    // Update expand button icons/text for the new chapter
    this.expandIconTargets.forEach(icon => {
      if (icon.dataset.cardPosition === position) {
        icon.setAttribute("data-lucide", "minimize-2")
      } else {
        icon.setAttribute("data-lucide", "maximize-2")
      }
    })
    this.expandTextTargets.forEach(text => {
      if (text.dataset.cardPosition === position) {
        text.textContent = "Collapse"
      } else {
        text.textContent = "Expand"
      }
    })

    // Highlight the active nav item
    this.chapterNavItemTargets.forEach(item => {
      if (item.dataset.chapterPosition === position) {
        item.classList.add("bg-primary-100")
      } else {
        item.classList.remove("bg-primary-100")
      }
    })

    // Refresh Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }

    // Scroll the visible chapter to the top of the writing area
    const container = document.getElementById(`chapter-${position}`)
    if (container && this.hasWritingAreaTarget) {
      this.writingAreaTarget.scrollTop = 0
    }
  }

  // === UI Toggle Methods ===

  toggleExpand(event) {
    const position = event.currentTarget.dataset.cardPosition
    const isCurrentlyExpanded = this.expandedChapterPosition === position

    if (isCurrentlyExpanded) {
      // Collapse: show all chapters and restore sidebar
      this.expandedChapterPosition = null

      // Show title page
      if (this.hasTitlePageTarget) {
        this.titlePageTarget.classList.remove("hidden")
      }

      // Show all chapter containers
      this.chapterContainerTargets.forEach(container => {
        container.classList.remove("hidden")
      })

      // Expand sidebar back (if it was minimized by expand)
      if (this.sidebarMinimizedByExpand && this.sidebarMinimized) {
        this.toggleSidebar()
        this.sidebarMinimizedByExpand = false
      }

      // Remove expanded styles from the chapter container
      this.chapterContainerTargets.forEach(container => {
        container.classList.remove("expanded-chapter")
      })

      // Update all expand buttons to show "Expand"
      this.expandIconTargets.forEach(icon => {
        icon.setAttribute("data-lucide", "maximize-2")
      })
      this.expandTextTargets.forEach(text => {
        text.textContent = "Expand"
      })
    } else {
      // Expand: minimize sidebar, hide other chapters, expand this one
      this.expandedChapterPosition = position

      // Minimize sidebar (if not already minimized)
      if (!this.sidebarMinimized) {
        this.sidebarMinimizedByExpand = true
        this.toggleSidebar()
      }

      // Hide title page
      if (this.hasTitlePageTarget) {
        this.titlePageTarget.classList.add("hidden")
      }

      // Hide all other chapters, expand this one
      this.chapterContainerTargets.forEach(container => {
        if (container.dataset.cardPosition === position) {
          container.classList.remove("hidden")
          container.classList.add("expanded-chapter")
        } else {
          container.classList.add("hidden")
        }
      })

      // Update this expand button to show "Collapse"
      this.expandIconTargets.forEach(icon => {
        if (icon.dataset.cardPosition === position) {
          icon.setAttribute("data-lucide", "minimize-2")
        } else {
          icon.setAttribute("data-lucide", "maximize-2")
        }
      })
      this.expandTextTargets.forEach(text => {
        if (text.dataset.cardPosition === position) {
          text.textContent = "Collapse"
        } else {
          text.textContent = "Expand"
        }
      })
    }

    // Refresh Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }

  toggleSidebar() {
    this.sidebarMinimized = !this.sidebarMinimized

    if (this.hasSidebarTarget) {
      if (this.sidebarMinimized) {
        // Minimize sidebar
        this.sidebarTarget.classList.remove("w-64")
        this.sidebarTarget.classList.add("w-20")

        // Hide full content, show minimal
        if (this.hasSidebarTitleTarget) this.sidebarTitleTarget.classList.add("hidden")
        if (this.hasSidebarCountTarget) this.sidebarCountTarget.classList.add("hidden")
        if (this.hasSidebarFooterTarget) this.sidebarFooterTarget.classList.add("hidden")
        if (this.hasSidebarFooterMinimalTarget) this.sidebarFooterMinimalTarget.classList.remove("hidden")

        this.chapterNavFullTargets.forEach(el => el.classList.add("hidden"))
        this.chapterNavMinimalTargets.forEach(el => el.classList.remove("hidden"))

        // Add class to writing area for expansion
        if (this.hasWritingAreaTarget) {
          this.writingAreaTarget.classList.add("sidebar-minimized")
        }

        // Update icon
        if (this.hasSidebarToggleIconTarget) {
          this.sidebarToggleIconTarget.setAttribute("data-lucide", "panel-left-open")
        }
      } else {
        // Expand sidebar
        this.sidebarTarget.classList.remove("w-20")
        this.sidebarTarget.classList.add("w-64")

        // Show full content, hide minimal
        if (this.hasSidebarTitleTarget) this.sidebarTitleTarget.classList.remove("hidden")
        if (this.hasSidebarCountTarget) this.sidebarCountTarget.classList.remove("hidden")
        if (this.hasSidebarFooterTarget) this.sidebarFooterTarget.classList.remove("hidden")
        if (this.hasSidebarFooterMinimalTarget) this.sidebarFooterMinimalTarget.classList.add("hidden")

        this.chapterNavFullTargets.forEach(el => el.classList.remove("hidden"))
        this.chapterNavMinimalTargets.forEach(el => el.classList.add("hidden"))

        // Remove class from writing area
        if (this.hasWritingAreaTarget) {
          this.writingAreaTarget.classList.remove("sidebar-minimized")
        }

        // Update icon
        if (this.hasSidebarToggleIconTarget) {
          this.sidebarToggleIconTarget.setAttribute("data-lucide", "panel-left-close")
        }
      }

      // Refresh Lucide icons
      if (window.lucide) {
        window.lucide.createIcons()
      }
    }
  }

  toggleCoachPanel() {
    this.coachPanelHidden = !this.coachPanelHidden

    if (this.hasCoachPanelTarget) {
      if (this.coachPanelHidden) {
        // Hide coach panel with smooth transition
        this.coachPanelTarget.classList.add("coach-panel-hidden")

        // Show the "AI Coach" button after transition
        if (this.hasShowCoachButtonTarget) {
          setTimeout(() => {
            this.showCoachButtonTarget.classList.remove("hidden")
          }, 300)
        }

        // Add margin to writing area to avoid overlap with button
        if (this.hasWritingAreaTarget) {
          this.writingAreaTarget.classList.add("coach-hidden")
        }

        // Hide feedback buttons in editors
        this.feedbackContainerTargets.forEach(el => el.classList.add("hidden"))
      } else {
        // Show coach panel with smooth transition
        this.coachPanelTarget.classList.remove("coach-panel-hidden")

        // Hide the "AI Coach" button immediately
        if (this.hasShowCoachButtonTarget) {
          this.showCoachButtonTarget.classList.add("hidden")
        }

        // Remove margin from writing area
        if (this.hasWritingAreaTarget) {
          this.writingAreaTarget.classList.remove("coach-hidden")
        }

        // Show feedback buttons in editors
        this.feedbackContainerTargets.forEach(el => el.classList.remove("hidden"))
      }
    }
  }

  // Toggle screenplay format preview for a scene
  togglePreview(event) {
    const cardId = event.currentTarget.dataset.cardId

    // Find the editor and preview container for this card
    const editor = this.editorTargets.find(el => el.dataset.cardId === cardId)
    const previewContainer = this.previewContainerTargets.find(el => el.dataset.cardId === cardId)
    const previewIcon = this.previewIconTargets.find(el => el.dataset.cardId === cardId)
    const previewText = this.previewTextTargets.find(el => el.dataset.cardId === cardId)

    if (!editor || !previewContainer) return

    const isShowingPreview = !previewContainer.classList.contains("hidden")

    if (isShowingPreview) {
      // Switch back to editor
      previewContainer.classList.add("hidden")
      editor.classList.remove("hidden")

      // Update button
      if (previewIcon) previewIcon.setAttribute("data-lucide", "eye")
      if (previewText) previewText.textContent = "Preview"
    } else {
      // Switch to preview - parse Fountain and render
      const text = editor.value || ""

      try {
        const output = fountain.parse(text)
        const previewContent = previewContainer.querySelector("[data-preview-content]")
        if (previewContent) {
          previewContent.innerHTML = output.html.script || "<p class='text-gray-400 italic'>No content to preview</p>"
        }
      } catch (e) {
        console.error("Fountain parsing error:", e)
        const previewContent = previewContainer.querySelector("[data-preview-content]")
        if (previewContent) {
          previewContent.innerHTML = "<p class='text-red-500'>Error parsing screenplay format</p>"
        }
      }

      // Show preview, hide editor
      editor.classList.add("hidden")
      previewContainer.classList.remove("hidden")

      // Update button
      if (previewIcon) previewIcon.setAttribute("data-lucide", "edit-3")
      if (previewText) previewText.textContent = "Edit"
    }

    // Refresh Lucide icons
    if (window.lucide) {
      window.lucide.createIcons()
    }
  }
}
